import React, { useState, useEffect } from 'react';
import { BookmarkPlus, Plus, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '../ui/popover';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import {
  fetchLists,
  createList,
  addEventToList,
  removeEventFromList,
  fetchListDetail,
} from '../../lib/meApi';

/**
 * Adds the event to one of the user's lists.
 * Opens a popover showing all the user's lists with checkmarks for ones containing the event.
 * Click a list to toggle the event in/out. Inline "Crear lista" to make a new one.
 */
const AddToListButton = ({
  eventId,
  isAuthenticated = false,
  onLoginRequired,
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  const [lists, setLists] = useState(null);
  const [eventInList, setEventInList] = useState({}); // {listId: true|false}
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  const loadLists = async () => {
    try {
      const allLists = await fetchLists();
      setLists(allLists);
      // For each list, fetch detail to know if this event is already there
      // (we just need item ids; lightweight enough for typical few-lists case)
      const map = {};
      await Promise.all(
        allLists.map(async (lst) => {
          try {
            const detail = await fetchListDetail(lst.id);
            map[lst.id] = (detail.items || []).some(ev => ev.id === eventId);
          } catch {
            map[lst.id] = false;
          }
        })
      );
      setEventInList(map);
    } catch {
      setLists([]);
    }
  };

  const handleOpenChange = (o) => {
    if (o && !isAuthenticated) {
      onLoginRequired?.();
      toast.info('Inicia sesión para guardar en listas.');
      return;
    }
    setOpen(o);
    if (o && lists === null) loadLists();
  };

  const toggleList = async (list) => {
    if (busy) return;
    const already = eventInList[list.id];
    setBusy(true);
    // Optimistic
    setEventInList({ ...eventInList, [list.id]: !already });
    try {
      if (already) {
        await removeEventFromList(list.id, eventId);
        toast.success(`Quitado de ${list.name}`);
      } else {
        await addEventToList(list.id, eventId);
        toast.success(`Guardado en ${list.name}`);
      }
      setOpen(false);
    } catch (e) {
      setEventInList({ ...eventInList, [list.id]: already });
      toast.error('No se pudo actualizar la lista');
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const lst = await createList({ name: newName.trim(), emoji: '📌' });
      // Add the event to the new list immediately
      await addEventToList(lst.id, eventId);
      toast.success(`Guardado en "${lst.name}"`);
      setNewName('');
      setCreating(false);
      // Reload lists with the new one selected
      const allLists = await fetchLists();
      setLists(allLists);
      setEventInList({ ...eventInList, [lst.id]: true });
      setOpen(false);
    } catch {
      toast.error('No se pudo crear la lista');
    }
  };

  const size = compact ? 'w-8 h-8' : 'w-10 h-10';
  const iconSize = compact ? 'w-4 h-4' : 'w-5 h-5';

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Agregar a una lista"
          aria-label="Agregar a una lista"
          data-testid={`engagement-list-${eventId}`}
          className={`${size} rounded-full backdrop-blur-md bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition-all hover:scale-110 active:scale-95`}
        >
          <BookmarkPlus className={`${iconSize} text-gray-700`} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="end" data-testid="add-to-list-popover">
        <div className="p-3 border-b">
          <p className="font-semibold text-[#064E3B] text-sm">Guardar en una lista</p>
          <p className="text-xs text-gray-500 mt-0.5">Elige una o crea una nueva</p>
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {lists === null ? (
            <div className="flex items-center justify-center py-6 text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          ) : lists.length === 0 ? (
            <p className="text-center py-4 text-xs text-gray-400">Aún no tienes listas. Crea la primera.</p>
          ) : (
            lists.map(lst => {
              const checked = !!eventInList[lst.id];
              return (
                <button
                  key={lst.id}
                  type="button"
                  onClick={() => toggleList(lst)}
                  disabled={busy}
                  data-testid={`add-to-list-row-${lst.id}`}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 transition-colors disabled:opacity-60 text-left"
                >
                  <span className="text-xl shrink-0">{lst.emoji}</span>
                  <span className="flex-1 truncate text-sm font-medium text-[#064E3B]">{lst.name}</span>
                  {checked && <Check className="w-4 h-4 text-[#00CC66] shrink-0" />}
                </button>
              );
            })
          )}
        </div>
        <div className="border-t p-2">
          {creating ? (
            <div className="flex gap-1">
              <Input
                placeholder="Nombre de la lista"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') { setNewName(''); setCreating(false); } }}
                autoFocus
                className="h-8 text-sm rounded-lg"
                data-testid="add-to-list-new-input"
              />
              <Button
                size="sm"
                onClick={handleCreate}
                disabled={!newName.trim()}
                className="rounded-lg h-8 px-3 bg-[#00CC66] hover:bg-[#00B359]"
                data-testid="add-to-list-new-confirm"
              >
                OK
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setCreating(true)}
              className="w-full justify-start rounded-lg text-[#00CC66] hover:bg-emerald-50"
              data-testid="add-to-list-new-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear nueva lista
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default AddToListButton;
